import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

export default function SearchRank({ currentMonthStr, top1Point }) {
  const [email, setEmail] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Mock data for fallback since we can't guarantee Supabase view exists
  const mockSearchData = {
    'test@example.com': { rank: 12, total_points: 450, member_name: 'テストユーザー' }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const { data, error } = await supabase
        .from('remo_monthly_ranking')
        .select('*')
        .eq('year_month', currentMonthStr)
        .eq('member_email', email)
        .single();

      if (error) {
        // Fallback to mock data for demonstration if view doesn't exist
        if (mockSearchData[email]) {
          setResult(mockSearchData[email]);
        } else {
          throw error;
        }
      } else if (data) {
        setResult(data);
      } else {
        setError('見つかりませんでした。');
      }
    } catch (err) {
      console.error('Search error:', err);
      // Fallback
      if (mockSearchData[email]) {
        setResult(mockSearchData[email]);
      } else {
        setError('データが見つからないか、エラーが発生しました。');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-brand-card rounded-xl p-4 mb-4 border border-brand-main/20">
      <h2 className="text-sm font-bold text-brand-main mb-3 flex items-center gap-2">
        <SafeIcon icon={FiIcons.FiSearch} /> 自分の順位を確認
      </h2>
      
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="relative flex-grow">
          <SafeIcon icon={FiIcons.FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="メールアドレスを入力"
            className="w-full bg-brand-dark border border-gray-700 rounded-lg py-2 pl-9 pr-3 text-sm text-white focus:outline-none focus:border-brand-main transition-colors"
          />
        </div>
        <button 
          type="submit"
          disabled={loading || !email}
          className="bg-brand-main hover:bg-[#008f51] disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors whitespace-nowrap"
        >
          {loading ? '検索中...' : '確認'}
        </button>
      </form>

      <AnimatePresence>
        {error && (
          <motion.p 
            initial={{ opacity: 0, height: 0 }} 
            animate={{ opacity: 1, height: 'auto' }} 
            exit={{ opacity: 0, height: 0 }}
            className="text-red-400 text-xs mt-2"
          >
            {error}
          </motion.p>
        )}

        {result && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: -10 }}
            className="mt-4 p-3 bg-[#1A1A2E] rounded-lg border border-brand-accent/30 text-center"
          >
            <p className="text-gray-300 text-sm mb-1">{result.member_name} さん</p>
            <p className="text-lg font-bold text-white mb-2">
              現在 <span className="text-2xl text-brand-accent mx-1">{result.rank}</span> 位 
              <span className="text-sm font-normal text-gray-400 ml-2">({result.total_points}pt)</span>
            </p>
            
            {result.rank > 1 && top1Point > 0 && (
              <p className="text-xs text-brand-main font-medium border-t border-gray-700 pt-2 mt-2 inline-block">
                1位まであと {top1Point - result.total_points} pt！
                <br/>毎日参加すれば追いつける！🔥
              </p>
            )}
            {result.rank === 1 && (
              <p className="text-xs text-brand-accent font-bold border-t border-gray-700 pt-2 mt-2 inline-block">
                現在トップ独走中！このままキープ！👑
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}